import { Container, Label, NumericInput } from '@playcanvas/pcui';
import { Entity, Mat4, Quat, TranslateGizmo, Vec3 } from 'playcanvas';

import { EntityTransformOp } from '../edit-ops';
import { Events } from '../events';
import { computeProjectedPolygonArea } from '../measurement-geometry';
import { Scene } from '../scene';
import { Splat } from '../splat';
import { Transform } from '../transform';
import { localize } from '../ui/localization';

const mat = new Mat4();
const mat1 = new Mat4();
const mat2 = new Mat4();
const mat3 = new Mat4();
const p = new Vec3();
const p0 = new Vec3();
const p1 = new Vec3();
const r = new Quat();
const s = new Vec3();

const t = new Transform();

class MeasureTransformHandler {
    activate() {}
    deactivate() {}
}

type MeasureMode = 'distance' | 'polyline' | 'area' | 'angle';

const formatLength = (value: number) => value.toFixed(2);
const SQFT_PER_SQM = 10.763910416709722;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const computeAngleDegrees = (a: [number, number, number], b: [number, number, number], c: [number, number, number]) => {
    const abx = a[0] - b[0];
    const aby = a[1] - b[1];
    const abz = a[2] - b[2];
    const cbx = c[0] - b[0];
    const cby = c[1] - b[1];
    const cbz = c[2] - b[2];

    const abLen = Math.sqrt(abx * abx + aby * aby + abz * abz);
    const cbLen = Math.sqrt(cbx * cbx + cby * cby + cbz * cbz);
    if (abLen <= 1e-8 || cbLen <= 1e-8) {
        return 0;
    }

    const dot = abx * cbx + aby * cby + abz * cbz;
    const cosTheta = clamp(dot / (abLen * cbLen), -1, 1);
    return Math.acos(cosTheta) * (180 / Math.PI);
};

const toAreaDisplay = (areaSquareMeters: number, units: 'm' | 'ft') => {
    if (units === 'ft') {
        return {
            value: areaSquareMeters * SQFT_PER_SQM,
            suffix: 'sq ft'
        };
    }

    return {
        value: areaSquareMeters,
        suffix: 'sq m'
    };
};

class MeasureTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, parent: HTMLElement, canvasContainer: Container) {
        // create svg
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.classList.add('tool-svg', 'hidden');
        svg.id = 'measure-tool-svg';
        parent.appendChild(svg);

        const ns = svg.namespaceURI;

        // create defs node
        const defs = document.createElementNS(ns, 'defs');

        // create line element
        const line = document.createElementNS(ns, 'line') as SVGLineElement;
        line.id = 'measure-line';
        defs.appendChild(line);

        const lineBottom = document.createElementNS(ns, 'use') as SVGUseElement;
        lineBottom.id = 'measure-line-bottom';
        lineBottom.setAttribute('href', '#measure-line');

        const lineTop = document.createElementNS(ns, 'use') as SVGUseElement;
        lineTop.id = 'measure-line-top';
        lineTop.setAttribute('href', '#measure-line');

        // create line ends
        const lineStart = document.createElementNS(ns, 'circle') as SVGCircleElement;
        lineStart.id = 'measure-line-start';

        const lineEnd = document.createElementNS(ns, 'circle') as SVGCircleElement;
        lineEnd.id = 'measure-line-end';

        const polylineSegments = document.createElementNS(ns, 'g') as SVGGElement;
        polylineSegments.id = 'measure-polyline-segments';

        const polylineLabels = document.createElementNS(ns, 'g') as SVGGElement;
        polylineLabels.id = 'measure-polyline-labels';

        const angleRays = document.createElementNS(ns, 'g') as SVGGElement;
        angleRays.id = 'measure-angle-rays';

        const angleArc = document.createElementNS(ns, 'path') as SVGPathElement;
        angleArc.id = 'measure-angle-arc';

        const angleLabel = document.createElementNS(ns, 'text') as SVGTextElement;
        angleLabel.id = 'measure-angle-label';

        const areaFill = document.createElementNS(ns, 'polygon') as SVGPolygonElement;
        areaFill.id = 'measure-area-fill';

        const areaOutline = document.createElementNS(ns, 'polygon') as SVGPolygonElement;
        areaOutline.id = 'measure-area-outline';

        const areaLabel = document.createElementNS(ns, 'text') as SVGTextElement;
        areaLabel.id = 'measure-area-label';

        const measureLabel = document.createElementNS(ns, 'text') as SVGTextElement;
        measureLabel.id = 'measure-label';
        measureLabel.setAttribute('text-anchor', 'middle');
        measureLabel.setAttribute('visibility', 'hidden');

        svg.appendChild(defs);
        svg.appendChild(polylineSegments);
        svg.appendChild(polylineLabels);
        svg.appendChild(angleRays);
        svg.appendChild(angleArc);
        svg.appendChild(angleLabel);
        svg.appendChild(areaFill);
        svg.appendChild(areaOutline);
        svg.appendChild(areaLabel);
        svg.appendChild(measureLabel);
        svg.appendChild(lineBottom);
        svg.appendChild(lineTop);
        svg.appendChild(lineStart);
        svg.appendChild(lineEnd);

        // ui
        const lengthLabel = new Label({
            text: localize('measure.length')
        });

        const lengthInput = new NumericInput({
            width: 90,
            placeholder: 'm',
            precision: 2,
            min: 0.0001,
            value: 0
        });
        let suppressUI = 0;

        const selectToolbar = new Container({
            class: 'select-toolbar',
            hidden: true
        });

        selectToolbar.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });

        selectToolbar.append(lengthLabel);
        selectToolbar.append(lengthInput);
        canvasContainer.append(selectToolbar);

        const gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);
        const entity = new Entity('measureGizmoPivot');
        const transformHandler = new MeasureTransformHandler();

        let active = false;
        let splat: Splat | null = null;
        // In-progress measurement points live on the splat instance.
        // If selection changes away from the splat, `splat` becomes null, but the points can
        // still linger on the last splat we interacted with. Track it so we can clear properly
        // on mode/tool changes.
        let inProgressSplat: Splat | null = null;
        let measureMode: MeasureMode = ((events.invoke('measure.mode') ?? 'distance') as MeasureMode);

        const createLocalId = () => {
            const globalCrypto = globalThis as unknown as { crypto?: Crypto };
            const uuid = globalCrypto?.crypto && 'randomUUID' in globalCrypto.crypto ? (globalCrypto.crypto as any).randomUUID?.() : null;
            if (typeof uuid === 'string' && uuid.length > 0) {
                return uuid;
            }
            return `measurement-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        };

        let distanceMeasurementId: string | null = null;
        let angleMeasurementId: string | null = null;

        const isMeasurableSplat = (value: any): value is Splat => {
            return !!value && Array.isArray(value.measurePoints) && typeof value.measureSelection === 'number' && !!value.worldTransform;
        };

        const clearInProgressPoints = (target: Splat | null) => {
            if (!target || !isMeasurableSplat(target)) {
                return;
            }

            target.measureSelection = -1;
            target.measurePoints.length = 0;

            if (inProgressSplat === target) {
                inProgressSplat = null;
            }
        };

        // get world space point
        const getPoint = (index: number, result: Vec3) => {
            if (!splat) {
                return;
            }
            splat.worldTransform.transformPoint(splat.measurePoints[index], result);
        };

        const getPoint2d = (index: number, result: Vec3) => {
            getPoint(index, result);
            scene.camera.worldToScreen(result, result);
            result.x *= canvasContainer.dom.clientWidth;
            result.y *= canvasContainer.dom.clientHeight;
        };

        const clearChildren = (element: Element) => {
            while (element.firstChild) {
                element.removeChild(element.firstChild);
            }
        };

        const computePointsWorld = () => {
            if (!splat || !Array.isArray(splat.measurePoints)) {
                return [] as [number, number, number][];
            }

            return splat.measurePoints.map((point) => {
                splat!.worldTransform.transformPoint(point, p);
                return [p.x, p.y, p.z] as [number, number, number];
            });
        };

        const syncDistanceMeasurement = () => {
            if (!distanceMeasurementId || !splat || measureMode !== 'distance' || splat.measurePoints.length !== 2) {
                return;
            }

            const points = computePointsWorld();
            if (points.length < 2) {
                return;
            }

            const a = points[0];
            const b = points[1];
            const dx = b[0] - a[0];
            const dy = b[1] - a[1];
            const dz = b[2] - a[2];
            const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

            events.fire('measurements.update', {
                id: distanceMeasurementId,
                patch: {
                    kind: 'distance',
                    points: [a, b],
                    a,
                    b,
                    length
                }
            });
        };

        const syncAngleMeasurement = () => {
            if (!angleMeasurementId || !splat || measureMode !== 'angle' || splat.measurePoints.length !== 3) {
                return;
            }

            const points = computePointsWorld();
            if (points.length < 3) {
                return;
            }

            events.fire('measurements.update', {
                id: angleMeasurementId,
                patch: {
                    kind: 'angle',
                    points: [points[0], points[1], points[2]]
                }
            });
        };

        const resolveMeasureUnits = (): 'm' | 'ft' => {
            const units = events.invoke('measure.units');
            return units === 'ft' ? 'ft' : 'm';
        };

        const computeCurrentArea = () => {
            const points = computePointsWorld();
            return computeProjectedPolygonArea(points);
        };

        // Forward-declare so finish* helpers can reference it before its definition.
        let updateVisuals: () => void; // eslint-disable-line prefer-const

        const finishPolyline = () => {
            if (!splat || measureMode !== 'polyline' || splat.measurePoints.length < 2) {
                return;
            }

            const points = computePointsWorld();
            if (points.length < 2) {
                return;
            }

            events.fire('measurements.add', {
                kind: 'polyline',
                points
            });

            clearInProgressPoints(splat);
            updateVisuals();
        };

        const finishArea = () => {
            if (!splat || measureMode !== 'area' || splat.measurePoints.length < 3) {
                return;
            }

            const points = computePointsWorld();
            if (points.length < 3) {
                return;
            }

            const projected = computeProjectedPolygonArea(points);
            if (!projected) {
                return;
            }

            const units = resolveMeasureUnits();
            const displayArea = toAreaDisplay(projected.area, units);

            events.fire('measurements.add', {
                kind: 'area',
                points,
                area: displayArea.value,
                units
            });

            clearInProgressPoints(splat);
            updateVisuals();
        };

        const finishAngle = () => {
            if (!splat || measureMode !== 'angle' || splat.measurePoints.length < 3) {
                return;
            }

            const points = computePointsWorld();
            if (points.length < 3) {
                return;
            }

            events.fire('measurements.add', {
                kind: 'angle',
                points: [points[0], points[1], points[2]]
            });

            clearInProgressPoints(splat);
            updateVisuals();
        };

        updateVisuals = () => {
            gizmo.detach();

            const points = splat?.measurePoints;
            const selection = splat?.measureSelection ?? -1;

            if (active && splat && points && selection >= 0 && selection < points.length) {
                getPoint(selection, p);
                t.set(p, Quat.IDENTITY, Vec3.ONE);
                events.invoke('pivot').place(t);
                entity.setLocalPosition(p);
                gizmo.attach(entity);
            }

            if (measureMode === 'distance' && points && points.length === 2) {
                getPoint(0, p0);
                getPoint(1, p1);
                const len = p0.distance(p1);

                suppressUI++;
                lengthInput.value = len;
                lengthInput.enabled = true;
                suppressUI--;
            } else {
                lengthInput.enabled = false;
            }
        };

        gizmo.on('render:update', () => {
            scene.forceRender = true;
        });

        gizmo.on('transform:start', () => {
            events.invoke('pivot').start();
        });

        gizmo.on('transform:move', () => {
            events.invoke('pivot').moveTRS(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
        });

        gizmo.on('transform:end', () => {
            events.invoke('pivot').end();
        });

        events.on('selection.changed', (selection: any) => {
            splat = isMeasurableSplat(selection) ? selection : null;
            distanceMeasurementId = null;
            angleMeasurementId = null;
            if (active) {
                updateVisuals();
            }
        });

        events.on('measure.mode', (mode: MeasureMode) => {
            if (mode !== 'distance' && mode !== 'polyline' && mode !== 'area' && mode !== 'angle') {
                return;
            }

            const prevMode = measureMode;
            measureMode = mode;
            if (measureMode !== 'distance') {
                distanceMeasurementId = null;
            }
            if (measureMode !== 'angle') {
                angleMeasurementId = null;
            }
            if (prevMode !== measureMode) {
                // Switching measure modes should always start a fresh interaction.
                // We intentionally discard any in-progress points so previous geometry doesn't reappear
                // when the user returns to a mode.
                clearInProgressPoints(splat);
                if (inProgressSplat && inProgressSplat !== splat) {
                    clearInProgressPoints(inProgressSplat);
                }
            } else if (splat) {
                splat.measureSelection = -1;
            }
            updateVisuals();
            scene.forceRender = true;
        });

        events.on('pivot.started', () => {

        });

        events.on('pivot.moved', () => {
            if (active && splat && splat.measureSelection >= 0 && splat.measureSelection < splat.measurePoints.length) {
                const p = events.invoke('pivot').transform.position;
                mat.invert(splat.worldTransform);
                mat.transformPoint(p, splat.measurePoints[splat.measureSelection]);
            }
            scene.forceRender = true;
        });

        events.on('pivot.ended', () => {
            if (active && splat && splat.measureSelection >= 0 && splat.measureSelection < splat.measurePoints.length) {
                updateVisuals();
                syncDistanceMeasurement();
                syncAngleMeasurement();
            }
        });

        const origTransform = new Mat4();
        const origP = new Vec3();
        const origR = new Quat();
        const origS = new Vec3();
        const mid = new Vec3();
        let startLen = 0;

        const startScale = () => {
            if (!splat || !splat.measurePoints || splat.measurePoints.length !== 2) {
                return;
            }

            origTransform.copy(splat.worldTransform);
            origP.copy(splat.entity.getLocalPosition());
            origR.copy(splat.entity.getLocalRotation());
            origS.copy(splat.entity.getLocalScale());

            getPoint(0, p0);
            getPoint(1, p1);
            mid.sub2(p1, p0);
            startLen = mid.length();
            mid.mulScalar(0.5).add(p0);
        };

        // position and scale the splat according to the new length
        const applyLength = (newLength: number) => {
            if (!splat || !splat.measurePoints || splat.measurePoints.length !== 2 || newLength <= 0) {
                return;
            }

            const scale = newLength / startLen;

            // calculate mid point
            p.copy(mid);

            // construct a transform matrix that scales from p by len * 0.5
            mat1.setTranslate(-p.x, -p.y, -p.z);
            mat2.setScale(scale, scale, scale);
            mat3.setTranslate(p.x, p.y, p.z);

            mat.mul2(mat1, origTransform);
            mat.mul2(mat2, mat);
            mat.mul2(mat3, mat);

            mat.getTranslation(p);
            r.setFromMat4(mat);
            mat.getScale(s);

            splat.entity.setLocalPosition(p);
            splat.entity.setLocalRotation(r);
            splat.entity.setLocalScale(s);

            scene.forceRender = true;
        };

        const endScale = () => {
            if (!splat) {
                return;
            }
            const top = new EntityTransformOp({
                splat: splat,
                oldt: new Transform(origP, origR, origS),
                newt: new Transform(splat.entity.getLocalPosition(), splat.entity.getLocalRotation(), splat.entity.getLocalScale())
            });

            events.fire('edit.add', top);
            updateVisuals();
            syncDistanceMeasurement();
            syncAngleMeasurement();
        };

        let dragging = false;

        // handle length input updates
        lengthInput.on('slider:mousedown', () => {
            startScale();
            dragging = true;
        });
        lengthInput.on('change', (value) => {
            if (dragging) {
                applyLength(value);
            } else if (!suppressUI) {
                startScale();
                applyLength(value);
                endScale();
            }
        });
        lengthInput.on('slider:mouseup', () => {
            endScale();
            dragging = false;
        });

        events.on('select.delete', () => {
            if (active && splat && splat.measureSelection >= 0 && splat.measureSelection < splat.measurePoints.length) {
                splat.measurePoints.splice(splat.measureSelection, 1);
                splat.measureSelection--;
                if (splat.measurePoints.length === 0 && inProgressSplat === splat) {
                    inProgressSplat = null;
                }
                updateVisuals();
            }
        });

        const isPrimary = (e: PointerEvent) => {
            return e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary;
        };

        let clicked = false;

        const pointerdown = (e: PointerEvent) => {
            if (!clicked && isPrimary(e)) {
                clicked = true;
            }
        };

        const pointermove = (e: PointerEvent) => {
            clicked = false;
        };

        const pointerup = async (e: PointerEvent) => {
            // Right-click finishes polyline/area.
            if (active && e.pointerType === 'mouse' && e.button === 2) {
                if (splat && (measureMode === 'polyline' || measureMode === 'area')) {
                    if (measureMode === 'polyline') {
                        finishPolyline();
                    } else {
                        finishArea();
                    }
                }

                e.preventDefault();
                e.stopPropagation();
                clicked = false;
                return;
            }

            if (!clicked || !isPrimary(e)) {
                return;
            }

            clicked = false;

            const pickMode = (events.invoke('measure.splatPickMode') ?? 'normal') as 'normal' | 'center' | 'none';

            if (!splat && pickMode !== 'none') {
                const minOpacity = (events.invoke('measure.minSplatOpacity') ?? 0.5) as number;
                const baseOptions = {
                    strictMinSplatOpacity: true,
                    minSplatOpacity: minOpacity,
                    splatPickSize: pickMode === 'center' ? 5 : 1,
                    meshPick: 'closest' as const
                };
                let result = await scene.camera.intersect(
                    e.offsetX / canvasContainer.dom.clientWidth,
                    e.offsetY / canvasContainer.dom.clientHeight,
                    baseOptions
                );

                if (!result) {
                    result = await scene.camera.intersect(
                        e.offsetX / canvasContainer.dom.clientWidth,
                        e.offsetY / canvasContainer.dom.clientHeight,
                        {
                            splatPickSize: baseOptions.splatPickSize,
                            meshPick: 'fallback' as const
                        }
                    );
                }

                splat = result?.splat ?? null;
            }

            if (!splat) {
                return;
            }

            let closestIdx = -1;

            // check for intersection with existing point
            for (let i = 0; i < splat.measurePoints.length; i++) {
                getPoint2d(i, p);

                if (Math.abs(p.x - e.offsetX) < 8 && Math.abs(p.y - e.offsetY) < 8) {
                    closestIdx = i;
                    break;
                }
            }

            if (closestIdx >= 0) {
                // Clicking the starting point closes/ends polyline & area.
                if (closestIdx === 0 && splat) {
                    if (measureMode === 'polyline' && splat.measurePoints.length >= 2) {
                        finishPolyline();
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                    }

                    if (measureMode === 'area' && splat.measurePoints.length >= 3) {
                        finishArea();
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                    }
                }

                splat.measureSelection = closestIdx;
                updateVisuals();
                return;
            }

            // Distance behavior: after placing the two points, a third click (not on endpoints)
            // clears the overlay so a new measurement can start.
            if (measureMode === 'distance' && splat.measurePoints.length === 2) {
                distanceMeasurementId = null;
                clearInProgressPoints(splat);
                updateVisuals();

                e.preventDefault();
                e.stopPropagation();
                return;
            }

            // Angle behavior: after placing 3 points, the next click clears the overlay
            // so a new angle measurement can start.
            if (measureMode === 'angle' && splat.measurePoints.length === 3) {
                angleMeasurementId = null;
                clearInProgressPoints(splat);
                updateVisuals();

                e.preventDefault();
                e.stopPropagation();
                return;
            }

            const allowAddPoint = measureMode === 'polyline' || measureMode === 'area' ||
                (measureMode === 'distance' && splat.measurePoints.length < 2) ||
                (measureMode === 'angle' && splat.measurePoints.length < 3);

            if (allowAddPoint) {
                const normalizedX = e.offsetX / canvasContainer.dom.clientWidth;
                const normalizedY = e.offsetY / canvasContainer.dom.clientHeight;

                // Mesh only: use mesh raycast to place points.
                // Other modes: use legacy splat depth picking.
                const minOpacity = (events.invoke('measure.minSplatOpacity') ?? 0.5) as number;
                let result = pickMode === 'none' ?
                    await scene.camera.intersect(normalizedX, normalizedY, { meshPick: 'only' }) :
                    await scene.camera.intersect(
                        normalizedX,
                        normalizedY,
                        {
                            strictMinSplatOpacity: true,
                            minSplatOpacity: minOpacity,
                            splatPickSize: pickMode === 'center' ? 5 : 1,
                            meshPick: 'closest'
                        }
                    );

                if (!result && pickMode !== 'none') {
                    result = await scene.camera.intersect(
                        normalizedX,
                        normalizedY,
                        {
                            splatPickSize: pickMode === 'center' ? 5 : 1,
                            meshPick: 'fallback'
                        }
                    );
                }

                if (result) {
                    mat.invert(splat.worldTransform);
                    mat.transformPoint(result.position, p);

                    if ((measureMode === 'polyline' || measureMode === 'area') && splat.measurePoints.length > 0) {
                        const minPoints = measureMode === 'area' ? 3 : 2;
                        const first = splat.measurePoints[0];
                        const closeThreshold = 0.05;
                        if (splat.measurePoints.length >= minPoints && first && p.distance(first) <= closeThreshold) {
                            if (measureMode === 'polyline') {
                                finishPolyline();
                            } else {
                                finishArea();
                            }
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }
                    }

                    splat.measureSelection = splat.measurePoints.length;
                    splat.measurePoints.push(p.clone());
                    inProgressSplat = splat;

                    if (measureMode === 'distance' && splat.measurePoints.length === 2) {
                        const points = computePointsWorld();
                        if (points.length >= 2) {
                            const a = points[0];
                            const b = points[1];
                            const dx = b[0] - a[0];
                            const dy = b[1] - a[1];
                            const dz = b[2] - a[2];
                            const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

                            const nextId = distanceMeasurementId ?? createLocalId();
                            distanceMeasurementId = nextId;

                            events.fire('measurements.add', {
                                id: nextId,
                                kind: 'distance',
                                points: [a, b],
                                a,
                                b,
                                length
                            });
                        }

                        updateVisuals();
                    } else if (measureMode === 'angle' && splat.measurePoints.length === 3) {
                        const points = computePointsWorld();
                        if (points.length >= 3) {
                            const nextId = angleMeasurementId ?? createLocalId();
                            angleMeasurementId = nextId;
                            events.fire('measurements.add', {
                                id: nextId,
                                kind: 'angle',
                                points: [points[0], points[1], points[2]]
                            });
                        }

                        // Keep the overlay visible until the user clicks elsewhere.
                        updateVisuals();
                    } else {
                        updateVisuals();
                    }
                }
            }

            e.preventDefault();
            e.stopPropagation();
        };

        const dblclick = (e: MouseEvent) => {
            if (!active || !splat || (measureMode !== 'polyline' && measureMode !== 'area')) {
                return;
            }

            if (measureMode === 'polyline') {
                finishPolyline();
            } else {
                finishArea();
            }
            e.preventDefault();
            e.stopPropagation();
        };

        const keydown = (e: KeyboardEvent) => {
            if (!active || !splat || (measureMode !== 'polyline' && measureMode !== 'area')) {
                return;
            }

            const isEnter = e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter';
            const isSpace = e.key === ' ' || e.code === 'Space';
            if (isEnter || isSpace) {
                if (measureMode === 'polyline') {
                    finishPolyline();
                } else {
                    finishArea();
                }
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const contextmenu = (e: MouseEvent) => {
            if (!active) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
        };

        events.on('postrender', () => {
            const points = splat?.measurePoints;

            if (active && points) {
                clearChildren(polylineSegments);
                clearChildren(polylineLabels);
                clearChildren(angleRays);
                angleArc.setAttribute('visibility', 'hidden');
                angleLabel.setAttribute('visibility', 'hidden');
                areaFill.setAttribute('visibility', 'hidden');
                areaOutline.setAttribute('visibility', 'hidden');
                areaLabel.setAttribute('visibility', 'hidden');
                measureLabel.setAttribute('visibility', 'hidden');

                if (measureMode === 'distance') {
                    line.setAttribute('visibility', points.length > 1 ? 'visible' : 'hidden');

                    for (let i = 0; i < 2; i++) {
                        if (i < points.length) {
                            getPoint2d(i, p);

                            const x = p.x.toString();
                            const y = p.y.toString();

                            if (i === 0) {
                                line.setAttribute('x1', x);
                                line.setAttribute('y1', y);
                                lineStart.setAttribute('cx', x);
                                lineStart.setAttribute('cy', y);

                                lineStart.setAttribute('visibility', 'visible');
                            } else if (i === 1) {
                                line.setAttribute('x2', x);
                                line.setAttribute('y2', y);
                                lineEnd.setAttribute('cx', x);
                                lineEnd.setAttribute('cy', y);
                                lineEnd.setAttribute('visibility', 'visible');
                            }
                        } else {
                            if (i === 0) {
                                lineStart.setAttribute('visibility', 'hidden');
                            } else {
                                lineEnd.setAttribute('visibility', 'hidden');
                            }
                        }
                    }

                    if (points.length === 2) {
                        getPoint2d(0, p0);
                        getPoint2d(1, p1);
                        const midX = (p0.x + p1.x) * 0.5;
                        const midY = (p0.y + p1.y) * 0.5;

                        getPoint(0, p0);
                        getPoint(1, p1);
                        const len = p0.distance(p1);

                        measureLabel.setAttribute('x', midX.toString());
                        measureLabel.setAttribute('y', (midY - 18).toString());
                        measureLabel.textContent = formatLength(len);
                        measureLabel.setAttribute('visibility', 'visible');
                    }
                } else if (measureMode === 'polyline') {
                    line.setAttribute('visibility', 'hidden');
                    lineStart.setAttribute('visibility', points.length > 0 ? 'visible' : 'hidden');
                    lineEnd.setAttribute('visibility', points.length > 0 ? 'visible' : 'hidden');

                    if (points.length > 0) {
                        getPoint2d(0, p0);
                        lineStart.setAttribute('cx', p0.x.toString());
                        lineStart.setAttribute('cy', p0.y.toString());

                        getPoint2d(points.length - 1, p1);
                        lineEnd.setAttribute('cx', p1.x.toString());
                        lineEnd.setAttribute('cy', p1.y.toString());
                    }

                    let total = 0;

                    for (let i = 1; i < points.length; i++) {
                        getPoint2d(i - 1, p0);
                        getPoint2d(i, p1);

                        const segmentMidX = (p0.x + p1.x) * 0.5;
                        const segmentMidY = (p0.y + p1.y) * 0.5;

                        const segmentBottom = document.createElementNS(ns, 'line');
                        segmentBottom.setAttribute('class', 'measure-polyline-bottom');
                        segmentBottom.setAttribute('x1', p0.x.toString());
                        segmentBottom.setAttribute('y1', p0.y.toString());
                        segmentBottom.setAttribute('x2', p1.x.toString());
                        segmentBottom.setAttribute('y2', p1.y.toString());

                        const segmentTop = document.createElementNS(ns, 'line');
                        segmentTop.setAttribute('class', 'measure-polyline-top');
                        segmentTop.setAttribute('x1', p0.x.toString());
                        segmentTop.setAttribute('y1', p0.y.toString());
                        segmentTop.setAttribute('x2', p1.x.toString());
                        segmentTop.setAttribute('y2', p1.y.toString());

                        polylineSegments.appendChild(segmentBottom);
                        polylineSegments.appendChild(segmentTop);

                        getPoint(i - 1, p0);
                        getPoint(i, p1);
                        const segmentLength = p0.distance(p1);
                        total += segmentLength;
                    }

                    if (points.length > 1) {
                        getPoint2d(points.length - 1, p1);
                        const totalLabel = document.createElementNS(ns, 'text');
                        totalLabel.setAttribute('class', 'measure-total-label');
                        totalLabel.setAttribute('x', (p1.x + 14).toString());
                        totalLabel.setAttribute('y', (p1.y - 14).toString());
                        totalLabel.textContent = `Σ ${formatLength(total)}`;
                        polylineLabels.appendChild(totalLabel);
                    }
                } else if (measureMode === 'area') {
                    line.setAttribute('visibility', 'hidden');
                    lineStart.setAttribute('visibility', points.length > 0 ? 'visible' : 'hidden');
                    lineEnd.setAttribute('visibility', points.length > 0 ? 'visible' : 'hidden');

                    if (points.length > 0) {
                        getPoint2d(0, p0);
                        lineStart.setAttribute('cx', p0.x.toString());
                        lineStart.setAttribute('cy', p0.y.toString());

                        getPoint2d(points.length - 1, p1);
                        lineEnd.setAttribute('cx', p1.x.toString());
                        lineEnd.setAttribute('cy', p1.y.toString());
                    }

                    const projectedPoints2d: string[] = [];
                    for (let i = 0; i < points.length; i++) {
                        getPoint2d(i, p0);
                        projectedPoints2d.push(`${p0.x},${p0.y}`);
                    }

                    if (points.length > 1) {
                        for (let i = 1; i < points.length; i++) {
                            getPoint2d(i - 1, p0);
                            getPoint2d(i, p1);

                            const segmentBottom = document.createElementNS(ns, 'line');
                            segmentBottom.setAttribute('class', 'measure-polyline-bottom');
                            segmentBottom.setAttribute('x1', p0.x.toString());
                            segmentBottom.setAttribute('y1', p0.y.toString());
                            segmentBottom.setAttribute('x2', p1.x.toString());
                            segmentBottom.setAttribute('y2', p1.y.toString());

                            const segmentTop = document.createElementNS(ns, 'line');
                            segmentTop.setAttribute('class', 'measure-polyline-top');
                            segmentTop.setAttribute('x1', p0.x.toString());
                            segmentTop.setAttribute('y1', p0.y.toString());
                            segmentTop.setAttribute('x2', p1.x.toString());
                            segmentTop.setAttribute('y2', p1.y.toString());

                            polylineSegments.appendChild(segmentBottom);
                            polylineSegments.appendChild(segmentTop);
                        }
                    }

                    if (points.length >= 3) {
                        areaFill.setAttribute('points', projectedPoints2d.join(' '));
                        areaOutline.setAttribute('points', projectedPoints2d.join(' '));
                        areaFill.setAttribute('visibility', 'visible');
                        areaOutline.setAttribute('visibility', 'visible');

                        const projected = computeCurrentArea();
                        if (projected) {
                            p.set(projected.centroid[0], projected.centroid[1], projected.centroid[2]);
                            scene.camera.worldToScreen(p, p);
                            p.x *= canvasContainer.dom.clientWidth;
                            p.y *= canvasContainer.dom.clientHeight;

                            const units = resolveMeasureUnits();
                            const displayArea = toAreaDisplay(projected.area, units);

                            areaLabel.setAttribute('x', p.x.toString());
                            areaLabel.setAttribute('y', p.y.toString());
                            areaLabel.textContent = `${displayArea.value.toFixed(2)} ${displayArea.suffix}`;
                            areaLabel.setAttribute('visibility', 'visible');
                        }
                    }
                } else {
                    line.setAttribute('visibility', 'hidden');
                    lineStart.setAttribute('visibility', points.length > 0 ? 'visible' : 'hidden');
                    lineEnd.setAttribute('visibility', points.length > 0 ? 'visible' : 'hidden');

                    if (points.length > 0) {
                        getPoint2d(0, p0);
                        lineStart.setAttribute('cx', p0.x.toString());
                        lineStart.setAttribute('cy', p0.y.toString());

                        getPoint2d(points.length - 1, p1);
                        lineEnd.setAttribute('cx', p1.x.toString());
                        lineEnd.setAttribute('cy', p1.y.toString());
                    }

                    if (points.length >= 2) {
                        getPoint2d(1, p0);
                        getPoint2d(0, p1);

                        const rayABottom = document.createElementNS(ns, 'line');
                        rayABottom.setAttribute('class', 'measure-angle-ray-bottom');
                        rayABottom.setAttribute('x1', p0.x.toString());
                        rayABottom.setAttribute('y1', p0.y.toString());
                        rayABottom.setAttribute('x2', p1.x.toString());
                        rayABottom.setAttribute('y2', p1.y.toString());

                        const rayATop = document.createElementNS(ns, 'line');
                        rayATop.setAttribute('class', 'measure-angle-ray-top');
                        rayATop.setAttribute('x1', p0.x.toString());
                        rayATop.setAttribute('y1', p0.y.toString());
                        rayATop.setAttribute('x2', p1.x.toString());
                        rayATop.setAttribute('y2', p1.y.toString());

                        angleRays.appendChild(rayABottom);
                        angleRays.appendChild(rayATop);
                    }

                    if (points.length >= 3) {
                        getPoint2d(0, p0);
                        getPoint2d(1, p1);
                        getPoint2d(2, p);

                        const v1x = p0.x - p1.x;
                        const v1y = p0.y - p1.y;
                        const v2x = p.x - p1.x;
                        const v2y = p.y - p1.y;
                        const len1 = Math.hypot(v1x, v1y);
                        const len2 = Math.hypot(v2x, v2y);

                        const rayCBottom = document.createElementNS(ns, 'line');
                        rayCBottom.setAttribute('class', 'measure-angle-ray-bottom');
                        rayCBottom.setAttribute('x1', p1.x.toString());
                        rayCBottom.setAttribute('y1', p1.y.toString());
                        rayCBottom.setAttribute('x2', p.x.toString());
                        rayCBottom.setAttribute('y2', p.y.toString());

                        const rayCTop = document.createElementNS(ns, 'line');
                        rayCTop.setAttribute('class', 'measure-angle-ray-top');
                        rayCTop.setAttribute('x1', p1.x.toString());
                        rayCTop.setAttribute('y1', p1.y.toString());
                        rayCTop.setAttribute('x2', p.x.toString());
                        rayCTop.setAttribute('y2', p.y.toString());

                        angleRays.appendChild(rayCBottom);
                        angleRays.appendChild(rayCTop);

                        if (len1 > 1e-6 && len2 > 1e-6) {
                            const n1x = v1x / len1;
                            const n1y = v1y / len1;
                            const n2x = v2x / len2;
                            const n2y = v2y / len2;

                            const radius = Math.max(18, Math.min(48, Math.min(len1, len2) * 0.25));
                            const arcStartX = p1.x + n1x * radius;
                            const arcStartY = p1.y + n1y * radius;
                            const arcEndX = p1.x + n2x * radius;
                            const arcEndY = p1.y + n2y * radius;
                            const cross = n1x * n2y - n1y * n2x;
                            const sweepFlag = cross >= 0 ? 1 : 0;

                            angleArc.setAttribute('d', `M ${arcStartX} ${arcStartY} A ${radius} ${radius} 0 0 ${sweepFlag} ${arcEndX} ${arcEndY}`);
                            angleArc.setAttribute('visibility', 'visible');

                            const worldPoints = computePointsWorld();
                            const angleDegrees = worldPoints.length >= 3 ?
                                computeAngleDegrees(worldPoints[0], worldPoints[1], worldPoints[2]) :
                                0;

                            let bisectorX = n1x + n2x;
                            let bisectorY = n1y + n2y;
                            const bisectorLength = Math.hypot(bisectorX, bisectorY);
                            if (bisectorLength > 1e-6) {
                                bisectorX /= bisectorLength;
                                bisectorY /= bisectorLength;
                            } else {
                                bisectorX = -n1y;
                                bisectorY = n1x;
                            }

                            const labelRadius = radius + 18;
                            angleLabel.setAttribute('x', (p1.x + bisectorX * labelRadius).toString());
                            angleLabel.setAttribute('y', (p1.y + bisectorY * labelRadius).toString());
                            angleLabel.textContent = `${angleDegrees.toFixed(1)}°`;
                            angleLabel.setAttribute('visibility', 'visible');
                        }
                    }
                }
            } else {
                line.setAttribute('visibility', 'hidden');
                lineStart.setAttribute('visibility', 'hidden');
                lineEnd.setAttribute('visibility', 'hidden');
                angleArc.setAttribute('visibility', 'hidden');
                angleLabel.setAttribute('visibility', 'hidden');
                areaFill.setAttribute('visibility', 'hidden');
                areaOutline.setAttribute('visibility', 'hidden');
                areaLabel.setAttribute('visibility', 'hidden');
                measureLabel.setAttribute('visibility', 'hidden');
                clearChildren(polylineSegments);
                clearChildren(polylineLabels);
                clearChildren(angleRays);
            }
        });

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            if (camera.ortho) {
                gizmo.size = 1125 / canvas.clientHeight;
            } else {
                gizmo.size = 1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
            }
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);

        this.activate = () => {
            active = true;
            clearInProgressPoints(splat);
            if (inProgressSplat && inProgressSplat !== splat) {
                clearInProgressPoints(inProgressSplat);
            }
            distanceMeasurementId = null;
            angleMeasurementId = null;
            updateVisuals();
            canvasContainer.dom.addEventListener('pointerdown', pointerdown);
            canvasContainer.dom.addEventListener('pointermove', pointermove);
            canvasContainer.dom.addEventListener('pointerup', pointerup, true);
            canvasContainer.dom.addEventListener('dblclick', dblclick, true);
            canvasContainer.dom.addEventListener('contextmenu', contextmenu, true);
            window.addEventListener('keydown', keydown, true);
            selectToolbar.hidden = false;
            parent.style.display = 'block';
            parent.classList.add('noevents');
            svg.classList.remove('hidden');

            events.fire('transformHandler.push', transformHandler);
        };

        this.deactivate = () => {
            active = false;
            distanceMeasurementId = null;
            angleMeasurementId = null;
            clearInProgressPoints(splat);
            if (inProgressSplat && inProgressSplat !== splat) {
                clearInProgressPoints(inProgressSplat);
            }
            updateVisuals();
            canvasContainer.dom.removeEventListener('pointerdown', pointerdown);
            canvasContainer.dom.removeEventListener('pointermove', pointermove);
            canvasContainer.dom.removeEventListener('pointerup', pointerup, true);
            canvasContainer.dom.removeEventListener('dblclick', dblclick, true);
            canvasContainer.dom.removeEventListener('contextmenu', contextmenu, true);
            window.removeEventListener('keydown', keydown, true);
            selectToolbar.hidden = true;
            parent.style.display = 'none';
            parent.classList.remove('noevents');
            svg.classList.add('hidden');

            events.fire('transformHandler.pop');
        };
    }
}

export { MeasureTool };
